import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type ContractStatus =
  | 'draft'
  | 'under_review'
  | 'approved'
  | 'active'
  | 'expiring'
  | 'expired'
  | 'terminated'
  | 'archived';

export type GateDecision =
  | 'APPROVED'
  | 'APPROVED_WITH_ALERTS'
  | 'REJECTED'
  | 'MANUAL_REVIEW_REQUIRED';

interface ContractStatusBadgeProps {
  status: ContractStatus;
  className?: string;
}

interface GateDecisionBadgeProps {
  decision: GateDecision | null | undefined;
  className?: string;
}

const STATUS_CONFIG: Record<
  ContractStatus,
  { label: string; className: string }
> = {
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground border-muted' },
  under_review: { label: 'Under Review', className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300' },
  approved: { label: 'Approved', className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300' },
  active: { label: 'Active', className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200' },
  expiring: { label: 'Expiring', className: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300' },
  expired: { label: 'Expired', className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300' },
  terminated: { label: 'Terminated', className: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900 dark:text-red-200' },
  archived: { label: 'Archived', className: 'bg-muted/50 text-muted-foreground border-muted' },
};

const GATE_DECISION_CONFIG: Record<
  GateDecision,
  { label: string; className: string }
> = {
  APPROVED: { label: 'Approved', className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300' },
  APPROVED_WITH_ALERTS: { label: 'Approved w/ Alerts', className: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300' },
  REJECTED: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300' },
  MANUAL_REVIEW_REQUIRED: { label: 'Manual Review', className: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300' },
};

export function ContractStatusBadge({ status, className }: ContractStatusBadgeProps) {
  // Defensive lookup: status may come from unvalidated backend payloads.
  const config =
    (STATUS_CONFIG as Record<string, (typeof STATUS_CONFIG)[ContractStatus] | undefined>)[status] ??
    STATUS_CONFIG.draft;
  return (
    <Badge
      variant="outline"
      className={cn(config.className, className)}
      aria-label={`Status: ${config.label}`}
    >
      {config.label}
    </Badge>
  );
}

export function GateDecisionBadge({ decision, className }: GateDecisionBadgeProps) {
  if (!decision) {
    return (
      <span className={cn('text-sm text-muted-foreground', className)} aria-label="Gate decision: none">
        —
      </span>
    );
  }
  const config = GATE_DECISION_CONFIG[decision];
  return (
    <Badge
      variant="outline"
      className={cn(config.className, className)}
      aria-label={`Gate decision: ${config.label}`}
    >
      {config.label}
    </Badge>
  );
}
